/**
 * Component: Custom Link Scroll
 * -----------------------------------------------------------------------------
 * A lightweight Custom Element that enables smooth scrolling to a section
 * specified in the `href` attribute of the internal <a> tag.
 *
 * Usage Example:
 *
 * <custom-link>
 *   <a href="#target-section" class="btn">
 *     Scroll to Section
 *   </a>
 * </custom-link>
 *
 * Notes:
 * - The href must point to a valid ID on the page (e.g. "#about").
 * - Uses native `scrollIntoView({ behavior: 'smooth' })` for smooth scrolling.
 *
 * @namespace CustomLink
 */

(() => {
    const SELECTOR = 'custom-link';
  
    if (!customElements.get(SELECTOR)) {
      class CustomLink extends HTMLElement {
        constructor() {
          super();
  
          this.handleClick = this.handleClick.bind(this);
        }
  
        /**
         * Called when the element is connected to the DOM.
         */
        connectedCallback() {
          this.anchor = this.querySelector('a[href^="#"]');
  
          if (this.anchor) {
            this.anchor.addEventListener('click', this.handleClick);
          }
        }
  
        /**
         * Called when the element is disconnected from the DOM.
         */
        disconnectedCallback() {
          if (this.anchor) {
            this.anchor.removeEventListener('click', this.handleClick);
          }
        }
  
        /**
         * Handles click on the internal <a> element.
         * Smoothly scrolls to the target element if it exists.
         * @param {MouseEvent} event
         */
        handleClick(event) {
          const targetId = this.anchor.getAttribute('href');
  
          if (!targetId || !targetId.startsWith('#')) {
            return;
          }
  
          const targetElement = document.querySelector(targetId);
  
          if (targetElement) {
            event.preventDefault();
  
            targetElement.scrollIntoView({
              behavior: 'smooth'
            });
          }
        }
      }
  
      customElements.define(SELECTOR, CustomLink);
    }
  })();